/** "Refresh electricity prices": runs the importers (utility registry, international tariffs, URDB, EIA, Ofgem) on a
 * light worker; at most one an hour. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, useToast } from "../../components/ui";
import { useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { useFmt } from "./format";

type Run = S["ElectricityRefreshRun"];

/** Importer labels: a catalog key where the label has words to translate, else the source's own (brand) name. */
const SOURCE_KEYS: Record<string, MessageKey> = {
  "registry:curated": "admin.refreshPrices.sourceRegistryCurated",
  "registry:eia861": "admin.refreshPrices.sourceRegistryEia861",
  "registry:ofgem": "admin.refreshPrices.sourceRegistryOfgem",
  "registry:oeb": "admin.refreshPrices.sourceRegistryOeb",
  "registry:cdr-register": "admin.refreshPrices.sourceRegistryCdr",
  "registry:mastr": "admin.refreshPrices.sourceRegistryMastr",
  "registry:cnmc": "admin.refreshPrices.sourceRegistryCnmc",
  "registry:eve": "admin.refreshPrices.sourceRegistryEve",
  "registry:mne": "admin.refreshPrices.sourceRegistryMne",
  "registry:acm": "admin.refreshPrices.sourceRegistryAcm",
  "registry:aneel": "admin.refreshPrices.sourceRegistryAneel",
  link: "admin.refreshPrices.sourceLinks",
  run: "admin.refreshPrices.sourceRun",
};
const SOURCE_NAMES: Record<string, string> = {
  eurostat: "Eurostat",
  "aer-cdr": "AER CDR",
  aneel: "ANEEL",
  eia861: "EIA-861",
  urdb: "URDB",
  eia: "EIA",
  ofgem: "Ofgem",
};
const TONES = {
  succeeded: "ok",
  partial: "warn",
  failed: "danger",
  queued: "info",
  running: "info",
} as const;

function RunSummary({ run }: { run: Run }) {
  const t = useT();
  const { date } = useFmt();
  const values = {
    trigger: run.trigger === "monthly" || run.trigger === "continue" ? run.trigger : run.requested_by ? "person" : "staff",
    who: run.requested_by ?? "",
    created: date(run.created_at, true),
    finished: date(run.finished_at, true),
  };
  return (
    <div className="stack" style={{ gap: 6 }}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <Badge tone={TONES[run.status]}>{t("admin.refreshPrices.runStatus", { status: run.status })}</Badge>
        <span className="small">{run.finished_at ? t("admin.refreshPrices.runFinished", values) : t("admin.refreshPrices.runStarted", values)}</span>
      </div>
      {run.sources.map((s) => (
        <div key={s.source} className="small">
          <FormattedMessage
            id="admin.refreshPrices.sourceResult"
            values={{
              source: SOURCE_KEYS[s.source] ? t(SOURCE_KEYS[s.source]) : (SOURCE_NAMES[s.source] ?? s.source),
              message: <span dir="auto">{s.message}</span>,
              b: (c) => <strong>{c}</strong>,
            }}
          />
          {!!s.changed?.length && (
            <span className="faint" dir="auto">
              {" "}
              {t("admin.refreshPrices.changed", { items: s.changed.slice(0, 5).join(", "), more: s.changed.length > 5 ? "yes" : "no" })}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function RefreshPrices() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const intl = useIntl();
  const { date } = useFmt();
  const status = useQuery({
    queryKey: ["electricity-refresh"],
    queryFn: () => unwrap(api.GET("/v1/admin/electricity-refresh")),
    refetchInterval: (q) =>
      ["queued", "running"].includes(q.state.data?.latest?.status ?? "")
        ? 5000
        : false,
  });
  const start = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/admin/electricity-refresh")),
    onSuccess: (data) => {
      qc.setQueryData(["electricity-refresh"], data);
      toast(t("admin.refreshPrices.queued"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const s = status.data;
  const busy = ["queued", "running"].includes(s?.latest?.status ?? "");
  return (
    <div style={{ marginBottom: 16 }}>
      <Card
        title={t("admin.refreshPrices.title")}
        description={t("admin.refreshPrices.description")}
        actions={
          <Button
            icon={<RefreshCw />}
            loading={start.isPending}
            disabled={!s || !!s.next_allowed_at}
            onClick={() => start.mutate()}
          >
            {t("admin.refreshPrices.refresh")}
          </Button>
        }
      >
        {status.error ? (
          <Alert tone="danger">{errorMessage(status.error)}</Alert>
        ) : s ? (
          <div className="stack" style={{ gap: 10 }}>
            {s.latest ? (
              <RunSummary run={s.latest} />
            ) : (
              <span className="small faint">{t("admin.refreshPrices.noRun")}</span>
            )}
            {s.next_allowed_at && (
              <span className="small faint">
                {t("admin.refreshPrices.nextAllowed", { busy: busy ? "yes" : "no", at: date(s.next_allowed_at, true) })}
              </span>
            )}
            {s.missing_keys.length > 0 && (
              <Alert tone="warn">
                <FormattedMessage
                  id="admin.refreshPrices.missingKeys"
                  values={{
                    keys: <span className="i18n-ltr">{intl.formatList(s.missing_keys, { type: "conjunction" })}</span>,
                    count: s.missing_keys.length,
                    code: (c) => <code>{c}</code>,
                  }}
                />
              </Alert>
            )}
          </div>
        ) : null}
      </Card>
    </div>
  );
}
